import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import QuestionRenderer from "./components/QuestionRenderer";
import ProgressBar from "./components/ProgressBar";
import "./dashboardStyle.css";
import { useDispatch, useSelector } from "react-redux";
import { saveSurvey, surveyDetail } from "../redux/slice/surveySlice";
import DashboardHeader from "./components/DashboardHeader";

const SurveyDetail = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const surveys = useSelector((state) => state.feedbackSurvey);
  const apiSurveyData = surveys?.detailSurvey?.data;

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [startTime] = useState(Date.now());

  useEffect(() => {
    dispatch(surveyDetail(surveyId));
  }, [dispatch, surveyId]);

  const transformApiDataToSurvey = (apiData) => {
    if (!apiData) return null;
    return {
      id: apiData._id,
      title: apiData.title,
      description: apiData.description,
      reward: apiData.reward?.amount || 0,
      currency:
        apiData.reward?.currency === "INR" ? "₹" : apiData.reward?.currency,
      questions: apiData.questions.map((q) => ({
        id: q._id,
        type:
          q.questionType === "image_upload"
            ? "image"
            : q.questionType === "text"
            ? q.required
              ? "text"
              : "text-long"
            : q.questionType,
        question: q.questionText,
        options: q.options?.map((opt) => opt.optionText) || [],
        placeholder:
          q.questionType === "text" ? "Enter your answer..." : undefined,
        accept: q.questionType === "image_upload" ? "image/*" : undefined,
        required: q.required,
      })),
    };
  };

  const survey = transformApiDataToSurvey(apiSurveyData);

  useEffect(() => {
    if (!survey && !surveys?.loading && apiSurveyData !== undefined) {
      alert("Survey not found!");
      navigate("/survey");
    }
  }, [survey, surveys?.loading, apiSurveyData, navigate]);

  if (surveys?.loading) {
    return (
      <div className="survey-detail-container">
        <DashboardHeader />
        <div className="container mt-4 min-h text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading survey...</p>
        </div>
      </div>
    );
  }

  if (!survey) return null;

  const currentQuestion = survey.questions[currentQuestionIndex];
  const totalQuestions = survey.questions.length;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;

  const handleAnswer = (answer) => {
    setAnswers({ ...answers, [currentQuestion.id]: answer });
  };

  const handleNext = () => {
    if (!isLastQuestion) setCurrentQuestionIndex(currentQuestionIndex + 1);
  };

  const isCurrentAnswerValid = () => {
    const answer = answers[currentQuestion.id];
    if (answer === null || answer === undefined || answer === "") return false;
    if (currentQuestion.type === "multiple" && Array.isArray(answer))
      return answer.length > 0;
    return true;
  };

  const handleSkip = () => {
    setAnswers({ ...answers, [currentQuestion.id]: "" });
    handleNext();
  };

  const getDeviceType = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(userAgent))
      return "tablet";
    if (
      /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
        userAgent
      )
    )
      return "mobile";
    return "desktop";
  };

  const getUserIP = async () => {
    try {
      const response = await fetch("https://api.ipify.org?format=json");
      const data = await response.json();
      return data.ip;
    } catch (error) {
      return "unavailable";
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const completionTime = Math.floor((Date.now() - startTime) / 1000);
      const ipAddress = await getUserIP();
      const responses = survey.questions.map((question) => ({
        questionId: question.id,
        questionType: question.type,
        answer: answers[question.id] || null,
      }));
      const submissionData = {
        completionTime,
        metadata: {
          ipAddress,
          source: "web",
          device: getDeviceType(),
        },
        responses,
        submittedAt: new Date().toISOString(),
      };
      await dispatch(
        saveSurvey({ surveyId: survey.id, responsedata: submissionData })
      );
      navigate("/survey");
    } catch (error) {
      alert("Failed to submit survey. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0)
      setCurrentQuestionIndex(currentQuestionIndex - 1);
  };

  return (
    <div className="survey-detail-container">
      <DashboardHeader />
      <div className="container mt-4 min-h">
        <ProgressBar current={currentQuestionIndex} total={totalQuestions} />
        <button
          className="btn btn-link"
          onClick={() => navigate("/survey")}
        >
          ← Back to Surveys
        </button>
        <div className="question-container card p-4 mt-4">
          <QuestionRenderer
            question={currentQuestion}
            answer={answers[currentQuestion.id]}
            onAnswer={handleAnswer}
            questionNumber={currentQuestionIndex + 1}
          />
          <div className="navigation-buttons mt-4 d-flex justify-content-between">
            <button
              className="btn btn-secondary"
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
            >
              Previous
            </button>
            {isLastQuestion ? (
              <button
                className="btn btn-success"
                onClick={handleSubmit}
                disabled={
                  (currentQuestion.required !== false &&
                    !isCurrentAnswerValid()) ||
                  isSubmitting
                }
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            ) : (
              <div>
                {currentQuestion.required === false && (
                  <button
                    className="btn btn-warning me-4"
                    onClick={handleSkip}
                  >
                    Skip
                  </button>
                )}
                <button
                  className="btn btn-primary"
                  onClick={handleNext}
                  disabled={!isCurrentAnswerValid()}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyDetail;
